import ExcelJS from 'exceljs';

/**
 * Shared styling primitives for our client-side .xlsx exports (inventory count review,
 * order-history product exports). Extracted from `inventoryCountExcelExport.ts` so every
 * export sheet looks like it came out of the same system: navy header bands, light
 * outlined tables, consistent number formats.
 *
 * Purely presentational — no domain types here.
 */

/** Deep navy used for header bands / table outlines. */
export const NAVY = 'FF0E1E2F';
export const NAVY_HEX = '#0E1E2F';
/** Very light navy tint for card fills / category bands / total rows. */
export const NAVY_SOFT = 'FFF2F5F8';
/** Muted blue-gray for secondary text on the navy band. */
export const NAVY_MUTED = 'FFB7C3D0';
export const TEXT_DARK = 'FF111827';
export const TEXT_GRAY = 'FF6B7280';
export const GREEN = 'FF059669';
export const RED = 'FFDC2626';
export const BORDER_GRAY = 'FFE5E7EB';

export const CURRENCY_FMT = '"€" #,##0.00';
export const PCT_FMT = '0.0%';
export const COUNT_FMT = '#,##0.##';

export const FONT = 'Calibri';

export type Ws = ExcelJS.Worksheet;

export function fill(color: string): ExcelJS.Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
}

export function bottomBorder(color: string): Partial<ExcelJS.Borders> {
  return { bottom: { style: 'thin', color: { argb: color } } };
}

/** Section/table title row: bold dark text with some air above. */
export function addTitleRow(ws: Ws, text: string): void {
  const row = ws.addRow([text]);
  row.getCell(1).font = { name: FONT, size: 12, bold: true, color: { argb: NAVY } };
  row.height = 22;
}

/** Table header row: brand-blue band with white bold labels. */
export function addHeaderRow(ws: Ws, labels: string[]): ExcelJS.Row {
  const row = ws.addRow(labels);
  for (let c = 1; c <= labels.length; c++) {
    const cell = row.getCell(c);
    cell.fill = fill(NAVY);
    cell.font = { name: FONT, size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.alignment = { vertical: 'middle' };
  }
  row.height = 20;
  return row;
}

/** Default data-row styling: 10pt dark text + light bottom border on filled columns. */
export function styleDataRow(row: ExcelJS.Row, columnCount: number): void {
  for (let c = 1; c <= columnCount; c++) {
    const cell = row.getCell(c);
    cell.font = { name: FONT, size: 10, color: { argb: TEXT_DARK } };
    cell.border = bottomBorder(BORDER_GRAY);
  }
}

/** Bold total row on a light navy band with a divider above. */
export function styleTotalRow(row: ExcelJS.Row, columnCount: number): void {
  for (let c = 1; c <= columnCount; c++) {
    const cell = row.getCell(c);
    cell.font = { name: FONT, size: 10, bold: true, color: { argb: TEXT_DARK } };
    cell.fill = fill(NAVY_SOFT);
    cell.border = { top: { style: 'thin', color: { argb: TEXT_GRAY } } };
  }
}

export function setCurrency(cell: ExcelJS.Cell, value: number | null | undefined): void {
  if (value == null || !Number.isFinite(value)) return;
  cell.value = value;
  cell.numFmt = CURRENCY_FMT;
}

/** Signed change cell: currency format, green when ≥ 0, red when < 0. */
export function setChange(cell: ExcelJS.Cell, value: number | null): void {
  if (value == null) return;
  cell.value = value;
  cell.numFmt = `+${CURRENCY_FMT};-${CURRENCY_FMT}`;
  cell.font = { name: FONT, size: 10, bold: true, color: { argb: value >= 0 ? GREEN : RED } };
}

export function setShare(cell: ExcelJS.Cell, share: number): void {
  cell.value = share;
  cell.numFmt = PCT_FMT;
}

export function setCount(cell: ExcelJS.Cell, value: number): void {
  cell.value = value;
  cell.numFmt = COUNT_FMT;
}

/** Excel sheet name: strip forbidden chars, cap at 31 chars, dedupe with " (2)" suffixes. */
export function sheetName(raw: string, used: Set<string>): string {
  const base = (raw.replace(/[\\/?*[\]:]/g, ' ').replace(/\s+/g, ' ').trim() || 'Sheet').slice(0, 31);
  let candidate = base;
  let i = 2;
  while (used.has(candidate.toLowerCase())) {
    const suffix = ` (${i})`;
    candidate = base.slice(0, 31 - suffix.length) + suffix;
    i++;
  }
  used.add(candidate.toLowerCase());
  return candidate;
}

/** Outline border drawn around a cell range, preserving each cell's other edges. */
export function outlineBox(
  ws: Ws,
  r1: number,
  c1: number,
  r2: number,
  c2: number,
  argb: string,
  style: ExcelJS.BorderStyle = 'thin'
): void {
  for (let r = r1; r <= r2; r++) {
    for (let c = c1; c <= c2; c++) {
      const edge = { style, color: { argb } };
      const cell = ws.getCell(r, c);
      cell.border = {
        ...cell.border,
        ...(r === r1 ? { top: edge } : {}),
        ...(r === r2 ? { bottom: edge } : {}),
        ...(c === c1 ? { left: edge } : {}),
        ...(c === c2 ? { right: edge } : {}),
      };
    }
  }
}

/** A chart rendered to a png on an offscreen canvas, sized in CSS pixels. */
export interface ChartPng {
  base64: string;
  width: number;
  height: number;
}

export interface DonutItem {
  label: string;
  value: number;
  color: string;
}

const DONUT_W = 480;

/**
 * Draw a dashboard-style donut (pie with a hole) + legend on an offscreen canvas (2x scale
 * for crispness) and return { base64, width, height } in CSS pixels, or null when it can't
 * render (no data / no canvas context). Drawing from data instead of screenshotting the
 * live chart keeps the export independent of what's currently on screen.
 */
export function drawDonutPng(items: DonutItem[], centerLabel: string): ChartPng | null {
  const total = items.reduce((sum, i) => sum + i.value, 0);
  if (items.length === 0 || total <= 0) return null;

  const scale = 2;
  const donutSize = 200;
  const pad = 16;
  const legendX = donutSize + pad * 2;
  const legendRowH = 22;
  const width = DONUT_W;
  const height = Math.max(donutSize + pad * 2, items.length * legendRowH + pad * 2);

  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.scale(scale, scale);

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);

  // Donut slices.
  const cx = pad + donutSize / 2;
  const cy = height / 2;
  const rOuter = donutSize / 2;
  const rInner = rOuter * 0.62;
  let angle = -Math.PI / 2;
  for (const item of items) {
    const sweep = (item.value / total) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, rOuter, angle, angle + sweep);
    ctx.closePath();
    ctx.fillStyle = item.color;
    ctx.fill();
    angle += sweep;
  }
  ctx.beginPath();
  ctx.arc(cx, cy, rInner, 0, Math.PI * 2);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();

  // Center total.
  ctx.fillStyle = NAVY_HEX;
  ctx.font = `600 15px ${FONT}, Helvetica, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(centerLabel, cx, cy);

  // Legend: swatch + label left, share right.
  const legendTop = (height - items.length * legendRowH) / 2;
  items.forEach((item, i) => {
    const y = legendTop + i * legendRowH + legendRowH / 2;
    ctx.fillStyle = item.color;
    ctx.beginPath();
    ctx.arc(legendX + 5, y, 5, 0, Math.PI * 2);
    ctx.fill();

    const pctText = `${((item.value / total) * 100).toFixed(1)}%`;
    ctx.font = `400 12px ${FONT}, Helvetica, Arial, sans-serif`;
    ctx.textAlign = 'right';
    ctx.fillStyle = '#6B7280';
    ctx.fillText(pctText, width - pad, y);

    ctx.textAlign = 'left';
    ctx.fillStyle = '#111827';
    const maxLabelW = width - pad - legendX - 60;
    let label = item.label;
    while (label.length > 3 && ctx.measureText(label).width > maxLabelW) label = `${label.slice(0, -2).trimEnd()}…`;
    ctx.fillText(label, legendX + 16, y);
  });

  return { base64: canvas.toDataURL('image/png').split(',')[1], width, height };
}

/** Add a rendered chart at the given anchor (0-based col/row); returns rows consumed. */
export function placeChartImage(
  wb: ExcelJS.Workbook,
  ws: Ws,
  png: ChartPng | null,
  col: number,
  rowIdx: number
): number {
  if (!png) return 0;
  const id = wb.addImage({ base64: png.base64, extension: 'png' });
  ws.addImage(id, { tl: { col, row: rowIdx }, ext: { width: png.width, height: png.height } });
  // Default row height is 15pt ≈ 20px.
  return Math.ceil(png.height / 20) + 1;
}

export const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/** Serialize the workbook and trigger a browser download. */
export async function downloadWorkbook(wb: ExcelJS.Workbook, filename: string): Promise<void> {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
